import { useCallback, useEffect, useState, type ComponentType } from "react";
import { MotionConfig } from "framer-motion";
import { useStore } from "jotai";
import { useAuth } from "common/hooks";

import {
  ONBOARDING_SIGN_UP_STEPS,
  firstUnansweredStep,
  sequenceForAnswers,
} from "./constants";
import { onboardingV2AnswersAtom, onboardingV2StepAtom } from "./state/atoms";
import {
  OnboardingSurfaceContext,
  useIsStepChange,
} from "./state/useOnboardingSurface";
import AccessNeedsStep from "./steps/AccessNeedsStep";
import AudienceStep from "./steps/AudienceStep";
import HearAboutStep from "./steps/HearAboutStep";
import UserTypeStep from "./steps/UserTypeStep";
import type { OnboardingStepName } from "./types";

const SIGN_UP_STEP_COMPONENTS: Partial<
  Record<OnboardingStepName, ComponentType>
> = {
  USER_TYPE: UserTypeStep,
  AUDIENCE: AudienceStep,
  ACCESS_NEEDS: AccessNeedsStep,
  HEAR_ABOUT: HearAboutStep,
};

interface SignUpOnboardingProps {
  /**
   * Every question has been answered. The answers are in
   * `onboardingV2AnswersAtom`; sending them is the caller's job, since only it
   * knows whether the account is signed in yet.
   */
  onComplete: () => void;
}

/**
 * The onboarding questions inside the auth modal, straight after an account is
 * created and before its email is verified (`16150:279245`, "Create an account
 * without completing onboarding questions").
 *
 * The same step components as the docked sheet and the modal, walking
 * `ONBOARDING_SIGN_UP_STEPS` instead — their answers land in the same atom, so
 * nothing is asked twice:
 *
 * - It **starts at the first question not yet answered**. Someone who got
 *   halfway through the docked sheet before signing up picks up from there.
 * - When it finishes it moves the main flow's step on to whatever sign-up did
 *   not ask — the city — so the modal that opens after the first sign-in asks
 *   only that.
 *
 * The step on screen is local state: sign-up is one sitting inside a modal,
 * and closing the modal part-way leaves the answers, not a position.
 */
const SignUpOnboarding = ({ onComplete }: SignUpOnboardingProps) => {
  const store = useStore();
  const { isLoggedIn } = useAuth();

  /*
   * Everything may be answered already: a signed-out visitor now gets "How did
   * you hear about us?" on the docked sheet too, so someone who finished that
   * sheet's questions before signing up has nothing left to be asked here.
   * Then this completes on mount and shows nothing.
   */
  const [firstStep] = useState(() =>
    firstUnansweredStep(
      ONBOARDING_SIGN_UP_STEPS,
      store.get(onboardingV2AnswersAtom),
    ),
  );
  const [step, setStep] = useState<OnboardingStepName>(
    firstStep ?? ONBOARDING_SIGN_UP_STEPS[ONBOARDING_SIGN_UP_STEPS.length - 1],
  );
  const [isDone, setIsDone] = useState(!firstStep);

  const stepIndex = ONBOARDING_SIGN_UP_STEPS.indexOf(step);
  const isStepChange = useIsStepChange(stepIndex, "signup");

  const goNext = useCallback(() => {
    const next = ONBOARDING_SIGN_UP_STEPS[stepIndex + 1];
    if (next) {
      setStep(next);
    } else {
      setIsDone(true);
    }
  }, [stepIndex]);

  /*
   * After a render rather than inside `goNext`: the last step writes its answer
   * and calls `goNext` in the same handler, and the caller reads the answers
   * when it is told — this way the write has landed by then.
   */
  useEffect(() => {
    if (!isDone) return;
    const answers = store.get(onboardingV2AnswersAtom);
    // Where the main flow resumes is picked here, before the sign-in lands;
    // it is the city either way, since sign-up has asked everything else.
    const { steps } = sequenceForAnswers(answers, { isSignedIn: !!isLoggedIn });
    store.set(
      onboardingV2StepAtom,
      firstUnansweredStep(steps, answers) ?? steps[0],
    );
    onComplete();
    // Once: `isDone` never goes back to false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone]);

  const Step = SIGN_UP_STEP_COMPONENTS[step];
  // Nothing to ask - @see `firstStep`. The host moves on once `onComplete` runs.
  if (!Step || !firstStep) return null;

  return (
    <MotionConfig reducedMotion="user">
      <OnboardingSurfaceContext.Provider
        value={{
          presentation: "signup",
          isStepChange,
          navigation: {
            stepNumber: stepIndex + 1,
            totalSteps: ONBOARDING_SIGN_UP_STEPS.length,
            goNext,
          },
        }}
      >
        <Step />
      </OnboardingSurfaceContext.Provider>
    </MotionConfig>
  );
};

export default SignUpOnboarding;
